import {
  Typography,
  Box,
  List,
  ListItem,
  makeStyles,
  IconButton,
  Collapse,
  Paper,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Link,
  Tooltip,
} from "@material-ui/core";
import { GetApp, Print as PrintIcon, Info as InfoIcon } from "@material-ui/icons";
import { useState } from "react";
import { ExpandMore, ExpandLess } from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  processingBox: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    padding: theme.spacing(3),
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    marginLeft: theme.spacing(1),
  },
  fileName: {
    marginLeft: theme.spacing(1),
    flexGrow: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  resultContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  resultHeader: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    textAlign: "left",
    borderBottom: "2px solid var(--ikaro-border-default)",
    paddingBottom: theme.spacing(1),
  },
  resultSubHeader: {
    fontWeight: "bold",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    textAlign: "left",
  },
  resultItem: {
    marginBottom: theme.spacing(1),
    textAlign: "left",
  },
  resultList: {
    textAlign: "left",
    paddingTop: theme.spacing(1),
  },
  listBullet: {
    display: "inline-block",
    // marginRight: theme.spacing(0.5),
    fontSize: "12px",
    color: theme.palette.primary.main,
  },
  // Estilos para o modal de detalhes do campo
  detailsDialogPaper: {
    maxWidth: '500px',
    width: '100%',
  },
  detailsDialogTitle: {
    padding: theme.spacing(1.5, 2),
    background: 'var(--ikaro-gradient-banner)',
    '& .MuiTypography-h6': {
      fontSize: '1rem',
      fontWeight: 600,
      color: 'var(--ikaro-text-white)',
    },
  },
  detailsContent: {
    padding: theme.spacing(2),
  },
  detailSection: {
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--ikaro-gray-dark)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    fontSize: '0.95rem',
    color: 'var(--ikaro-gray-darker)',
    backgroundColor: 'var(--ikaro-gray-light)',
    padding: theme.spacing(1, 1.5),
    borderRadius: 4,
    border: '1px solid var(--ikaro-gray-medium)',
  },
  detailValueEmpty: {
    fontStyle: 'italic',
    color: 'var(--ikaro-gray-dark)',
  },
  detailChip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  possibleValuesList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    backgroundColor: 'var(--ikaro-gray-light)',
    padding: theme.spacing(1, 1.5),
    borderRadius: 4,
    border: '1px solid var(--ikaro-gray-medium)',
  },
  possibleValueItem: {
    backgroundColor: 'var(--ikaro-orange-light)',
    color: 'var(--ikaro-gray-darker)',
    fontSize: '0.8rem',
  },
  booleanIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  booleanYes: {
    backgroundColor: 'var(--ikaro-status-success-bg)',
    color: 'var(--ikaro-status-success-text)',
  },
  booleanNo: {
    backgroundColor: 'var(--ikaro-gray-light)',
    color: 'var(--ikaro-gray-dark)',
  },
  detailsDialogActions: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: 'var(--ikaro-bg-surface-secondary)',
    borderTop: '1px solid var(--ikaro-border-default)',
  },
  typeChip: {
    backgroundColor: 'var(--ikaro-status-running-bg)',
    height: 22,
    fontSize: '0.7rem',
  },
  // Estilo para label clicável
  clickableLabel: {
    cursor: 'pointer',
    color: 'var(--ikaro-orange-primary)',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '&:hover': {
      textDecoration: 'underline',
      color: 'var(--ikaro-orange-hover)',
    },
  },
  infoIcon: {
    fontSize: '0.875rem',
    opacity: 0.7,
  },
}));

const getDisplayLabel = (key, value) => {
  // Se o valor for um objeto e tiver um campo 'label', use-o
  if (value && typeof value === "object" && !Array.isArray(value)) {
    if (value.label) {
      return value.label;
    }

    // Verificar se o próprio valor tem uma key especial 'label' no nível superior
    // (pode ocorrer em templates específicos da API)
    if (
      Object.keys(value).includes("label") &&
      typeof value.label === "string"
    ) {
      return value.label;
    }
  }

  // Tentativa de formatar o nome da chave para exibição mais amigável
  // Transforma snake_case ou camelCase em palavras separadas com primeira letra maiúscula
  const formatted = key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^\w/, (c) => c.toUpperCase());

  return formatted;
};

// Função para buscar informações do campo no template navegando pelo caminho de chaves
// keyPath é um array de chaves originais (ex: ['processo', 'AreaDoDireitoNome'])
const getFieldInfoByKeyPath = (templateData, keyPath) => {
  if (!templateData || typeof templateData !== 'object' || !keyPath || keyPath.length === 0) {
    return null;
  }
  
  // Função auxiliar para verificar se um nó é uma definição de campo (folha)
  // Um nó folha tem 'type', 'label', 'description' como propriedades diretas
  const isFieldDefinition = (node) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return false;
    const keys = Object.keys(node);
    // Deve ter 'type' ou 'label' para ser uma definição de campo
    return keys.includes('type') || keys.includes('label');
  };
  
  // Função auxiliar para extrair metadados de um nó folha
  const extractFieldInfo = (node) => {
    if (!node || typeof node !== 'object') return null;
    // Só extrai se for uma definição de campo válida
    if (!isFieldDefinition(node)) return null;
    return {
      label: node.label || null,
      description: node.description || null,
      type: node.type || null,
      possible_values: node.possible_values || []
    };
  };
  
  // Navegar pelo caminho de chaves
  let currentNode = templateData;
  
  for (let i = 0; i < keyPath.length; i++) {
    const key = keyPath[i];
    
    if (!currentNode || typeof currentNode !== 'object') {
      return null;
    }
    
    // Se for array, pega o primeiro elemento (schema/definição)
    while (Array.isArray(currentNode)) {
      if (currentNode.length > 0) {
        currentNode = currentNode[0];
      } else {
        return null;
      }
    }
    
    // Verificar se a chave existe no nó atual
    if (currentNode && typeof currentNode === 'object' && key in currentNode) {
      currentNode = currentNode[key];
    } else {
      // Chave não encontrada no template
      return null;
    }
  }
  
  // Chegamos ao nó final
  if (!currentNode || typeof currentNode !== 'object') {
    return null;
  }
  
  // Se for array, pega o primeiro elemento
  while (Array.isArray(currentNode)) {
    if (currentNode.length > 0) {
      currentNode = currentNode[0];
    } else {
      return null;
    }
  }
  
  // Extrair metadados se for uma definição de campo
  return extractFieldInfo(currentNode);
};

// Função para mapear tipos para labels
const getTypeLabel = (type) => {
  const typeLabels = {
    string: 'Texto',
    text: 'Texto',
    int: 'Inteiro',
    float: 'Decimal',
    date: 'Data',
    time: 'Hora',
    date_time: 'Data e Hora',
    bool: 'Booleano',
    possible_values: 'Valores Possíveis',
  };
  return typeLabels[type] || type?.toUpperCase() || 'TEXTO';
};

const resultHTML = ({ result, showRawResult, rawResult, t }) => {
  const printResults = () => {
    alert(t("startingPrint"));
    if (!result) return;

    try {
      // Determinar qual resultado usar baseado na seleção do usuário
      const dataToPrint = showRawResult && rawResult ? rawResult : result;

      // Criamos um novo documento para impressão
      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        alert(t("allowPopups"));
        return;
      }

      // Criamos um novo conteúdo HTML para impressão, apenas com os resultados
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>eLaw GenAI - ${
            showRawResult && rawResult ? t("rawResult") : t("finalResult")
          }</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.5;
              padding: 20px;
              color: #333;
            }
            h1 {
              color: #1976d2;
              font-size: 24px;
              margin-bottom: 16px;
              padding-bottom: 8px;
              border-bottom: 2px solid #e0e0e0;
            }
            h2 {
              color: #333;
              font-size: 18px;
              margin-top: 16px;
              margin-bottom: 8px;
            }
            h3 {
              color: #555;
              font-size: 16px;
              margin-top: 12px;
              margin-bottom: 6px;
            }
            .result-item {
              margin-bottom: 12px;
            }
            .list-item {
              margin-bottom: 4px;
              margin-left: 24px;
              position: relative;
            }
            .list-item:before {
              content: "•";
              color: #1976d2;
              position: absolute;
              left: -12px;
            }
            .empty-list {
              font-style: italic;
              color: #777;
              margin-left: 12px;
            }
            .item-label {
              font-weight: bold;
            }
            .item-value {
              margin-left: 8px;
            }
            .parent-item {
              margin-bottom: 16px;
              border-left: 1px solid #e0e0e0;
              padding-left: 12px;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <h1>eLaw GenAI - ${
            showRawResult && rawResult ? t("rawResult") : t("finalResult")
          }</h1>
          <div id="print-content">${renderPrintContent(dataToPrint)}</div>
        </body>
        </html>
      `;

      // Escrevemos o conteúdo na nova janela
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Aguardamos o conteúdo ser carregado e impressão ser chamada
      printWindow.onload = function () {
        printWindow.print();
        // Fechar a janela após a impressão (opcional - em alguns navegadores o usuário decide)
        printWindow.onafterprint = function () {
          printWindow.close();
        };
      };
    } catch (error) {
      console.error("Erro ao imprimir resultados:", error);
      alert(t("printError"));
    }
  };

  // Função auxiliar para gerar o HTML para impressão
  const renderPrintContent = (data) => {
    if (!data || typeof data !== "object") {
      return `<div class="empty-list">${t("noResultsAvailable")}</div>`;
    }

    let html = "";

    Object.entries(data).forEach(([key, value]) => {
      html += renderPrintItem(key, value, 0);
    });

    return html;
  };

  // Função recursiva para renderizar itens para impressão
  const renderPrintItem = (key, value, depth = 0) => {
    // Obter o label para exibição
    const displayLabel = getDisplayLabel(key, value);
    let html = "";

    // Renderizar o item baseado no tipo
    if (value === null || value === undefined) {
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;
      if (depth === 0) {
        html += `<h2>${displayLabel}</h2><div><em>Não definido</em></div>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span> <em>Não definido</em></div>`;
      }
      html += "</div>";
    } else if (typeof value === "object" && !Array.isArray(value)) {
      // É um objeto
      const metadataFields = ["label", "type", "description"];
      const keys = Object.keys(value);
      const hasOnlyMetadata = keys.every((k) => metadataFields.includes(k));

      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;

      if (depth === 0) {
        html += `<h2>${displayLabel}</h2>`;

        // Mostrar metadados se disponíveis
        if (value.description) {
          html += `<div style="margin-top: -8px; color: #555;">${value.description}</div>`;
        } else if (value.type) {
          html += `<div style="margin-top: -8px; color: #555; font-style: italic;">Tipo: ${value.type}</div>`;
        }
      } else {
        html += `<div><span class="item-label">${displayLabel}</span></div>`;
      }

      if (!hasOnlyMetadata) {
        html += '<div class="parent-item">';
        Object.entries(value)
          .filter(
            ([childKey]) =>
              !metadataFields.includes(childKey) || keys.length === 1
          )
          .forEach(([childKey, childValue]) => {
            html += renderPrintItem(childKey, childValue, depth + 1);
          });
        html += "</div>";
      }

      html += "</div>";
    } else if (Array.isArray(value)) {
      // É um array
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;

      if (depth === 0) {
        html += `<h2>${displayLabel}</h2>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span></div>`;
      }

      if (value.length === 0) {
        // Array vazio - mostrar mensagem
        html += '<div class="empty-list">Nenhum item encontrado</div>';
      } else {
        // Renderizar itens do array
        value.forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            html += '<div class="list-item">';

            // Se o item tiver um label, usar como título
            if (item.label) {
              html += `<div style="font-weight: bold;">${item.label}</div>`;
            }

            // Renderizar os campos do objeto dentro do item da lista
            Object.entries(item)
              .filter(
                ([itemKey]) =>
                  itemKey !== "label" &&
                  itemKey !== "type" &&
                  itemKey !== "description"
              )
              .forEach(([itemKey, itemValue]) => {
                html += renderPrintItem(itemKey, itemValue, depth + 2);
              });

            html += "</div>";
          } else {
            html += `<div class="list-item">${String(item)}</div>`;
          }
        });
      }

      html += "</div>";
    } else {
      // É um valor primitivo
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;
      if (depth === 0) {
        html += `<h2>${displayLabel}</h2><div>${String(value)}</div>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span> <span class="item-value">${String(
          value
        )}</span></div>`;
      }
      html += "</div>";
    }

    return html;
  };

  return printResults;
};

// Função recursiva para renderizar os resultados em formato adequado
const RenderResultItem = ({
  keyName,
  value,
  depth = 0,
  path = "",
  keyPath = [], // Caminho de chaves originais desde a raiz (para correlação com template)
  showEmptyItems,
  templateData = null,
  onFieldClick = null,
}) => {
  const classes = useStyles();
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const isNodeExpanded = (nodePath) => {
    // Se o nó não está no Set, significa que ainda não foi clicado
    // Nesse caso, retornamos true para que todos comecem expandidos
    return !expandedNodes.has(nodePath);
  };

  const toggleNode = (nodePath) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodePath)) {
        newSet.delete(nodePath);
      } else {
        newSet.add(nodePath);
      }
      return newSet;
    });
  };

  const nodePath = path ? `${path}.${keyName}` : keyName;
  const isExpanded = isNodeExpanded(nodePath);

  // Obter o label para exibição visual (pode ser transformado)
  const displayLabel = getDisplayLabel(keyName, value);
  
  // Construir o caminho de chaves atual usando a CHAVE ORIGINAL (para correlação com template)
  const currentKeyPath = [...keyPath, keyName];
  
  // Buscar informações do campo no template usando o caminho de chaves originais
  const fieldInfo = getFieldInfoByKeyPath(templateData, currentKeyPath);
  // Só considera que tem informações úteis se encontrou e tem dados relevantes
  const hasFieldInfo = fieldInfo && typeof fieldInfo === 'object' && (
    fieldInfo.description || 
    fieldInfo.type ||
    (fieldInfo.possible_values && fieldInfo.possible_values.length > 0)
  );
  
  // Função para renderizar o label como link clicável (se tiver informações do template)
  const renderClickableLabel = (label, isHeader = false) => {
    if (hasFieldInfo && onFieldClick) {
      return (
        <Tooltip title="Clique para ver detalhes do campo">
          <Link
            component="button"
            className={classes.clickableLabel}
            onClick={(e) => {
              e.stopPropagation();
              onFieldClick({ name: keyName, displayLabel, ...fieldInfo });
            }}
            style={isHeader ? { fontSize: 'inherit', fontWeight: 'inherit' } : {}}
          >
            {label}
            <InfoIcon className={classes.infoIcon} />
          </Link>
        </Tooltip>
      );
    }
    return label;
  };

  // Definir o espaçamento para níveis hierárquicos
  const indentSize = depth * 16; // Mantido em 16px por nível para não ficar excessivo

  // Função auxiliar para renderizar o título do item com estilo consistente
  const renderItemTitle = (showValue = null) => {
    if (depth === 0) {
      // Estilo destacado para todos os itens de nível raiz
      return (
        <>
          <Typography
            variant="h6"
            className={classes.resultHeader}
            align="left"
          >
            {renderClickableLabel(displayLabel, true)}
          </Typography>
          {showValue !== null && (
            <Typography
              variant="body1"
              style={{
                marginTop: -4,
                marginBottom: 12,
                paddingLeft: 8,
                color: "var(--ikaro-text-secondary)",
                fontStyle:
                  showValue.startsWith("Tipo:") ||
                  showValue.startsWith("Lista com")
                    ? "italic"
                    : "normal",
              }}
            >
              {showValue}
            </Typography>
          )}
        </>
      );
    } else {
      // Estilo normal para os demais níveis
      return (
          <Typography
            variant="subtitle1"
            className={classes.resultSubHeader}
            align="left"
          >
            {renderClickableLabel(displayLabel)}
          </Typography>
      );
    }
  };

  // Função auxiliar para renderizar o conteúdo de um item
  const renderItemContent = (content) => {
    return (
      <Box
        ml={2}
        className="text-left"
        style={{
          borderLeft: "1px solid var(--ikaro-border-default)",
          paddingLeft: 8,
        }}
      >
        {content}
      </Box>
    );
  };

  // Renderizar diferentes tipos de valores
  if (value === null || value === undefined) {
    // Se não devemos mostrar itens vazios e não é um item raiz, retornar null
    if (!showEmptyItems && depth > 0) {
      return null;
    }

    return (
      <Box
        key={keyName}
        className={`${classes.resultItem} text-left`}
        style={{
          paddingLeft: indentSize,
          marginBottom: depth === 0 ? 24 : 8,
        }}
      >
        {depth === 0 ? (
          renderItemTitle()
        ) : (
          <Typography variant="body2" align="left">
            <strong>{renderClickableLabel(displayLabel)}:</strong> <em>Não definido</em>
          </Typography>
        )}
      </Box>
    );
  } else if (typeof value === "object" && !Array.isArray(value)) {
    // É um objeto
    // Determinar quais campos não mostrar diretamente (campos de metadados)
    const metadataFields = ["label", "type", "description"];

    // Se for um objeto que tem apenas os campos de metadados, mostrar diferente
    const keys = Object.keys(value);
    const hasOnlyMetadata = keys.every((k) => metadataFields.includes(k));

    if (hasOnlyMetadata && keys.length > 0) {
      // Mostrar os metadados como uma descrição
      let description = "";
      if (value.description) {
        description = value.description;
      } else if (value.type) {
        description = `Tipo: ${value.type}`;
      }

      return (
        <Box
          key={keyName}
          className={`${classes.resultItem} text-left`}
          style={{
            paddingLeft: indentSize,
            marginBottom: depth === 0 ? 24 : 8,
          }}
        >
          {depth === 0 ? (
            renderItemTitle(description ? description : null)
          ) : (
            <Typography variant="body2" align="left">
              <strong>{renderClickableLabel(displayLabel)}:</strong>
              {description && (
                <em className="ml-8" style={{ color: "var(--ikaro-text-muted)" }}>{description}</em>
              )}
            </Typography>
          )}
        </Box>
      );
    }

    // Se não é um objeto que tem apenas metadados, renderizar normalmente com filhos
    // Vamos verificar se tem algum metadado importante para mostrar
    let metadataInfo = "";
    if (value.type) {
      metadataInfo = `Tipo: ${value.type}`;
    }
    if (value.description) {
      metadataInfo = metadataInfo
        ? `${metadataInfo}, ${value.description}`
        : value.description;
    }

    // Filtrar os filhos para remover itens vazios se necessário
    const filteredEntries = Object.entries(value)
      .filter(
        ([childKey]) => !metadataFields.includes(childKey) || keys.length === 1
      )
      .map(([childKey, childValue]) => (
        <RenderResultItem
          key={childKey}
          keyName={childKey}
          value={childValue}
          depth={depth + 1}
          path={nodePath}
          keyPath={currentKeyPath}
          showEmptyItems={showEmptyItems}
          templateData={templateData}
          onFieldClick={onFieldClick}
        />
      ))
      .filter(Boolean); // Remove nulls (itens vazios filtrados)

    // Se não temos filhos para mostrar e não devemos mostrar itens vazios, retornar null
    if (!showEmptyItems && filteredEntries.length === 0 && depth > 0) {
      return null;
    }

    return (
      <Box
        key={keyName}
        className="text-left"
        style={{
          paddingLeft: indentSize,
          marginBottom: depth === 0 ? 24 : 8,
        }}
      >
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={() => toggleNode(nodePath)}
            className="mr-4"
            style={{ padding: 4 }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {depth === 0 ? (
            renderItemTitle(metadataInfo ? metadataInfo : null)
          ) : (
            <Typography
              variant="subtitle1"
              className="text-left"
              align="left"
            >
              {renderClickableLabel(displayLabel)}
            </Typography>
          )}
        </Box>
        <Collapse in={isExpanded}>
          {renderItemContent(filteredEntries)}
        </Collapse>
      </Box>
    );
  } else if (Array.isArray(value)) {
    // É um array
    // Filtrar itens vazios se necessário
    const filteredItems =
      value.length > 0
        ? value
            .map((item, index) => {
              if (typeof item === "object" && item !== null) {
                const renderedItem = (
                  <ListItem
                    key={index}
                    dense
                    disableGutters
                    className="text-left mb-8"
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      className="text-left"
                      style={{ width: "100%" }}
                    >
                      <span
                        className={`${classes.listBullet} mr-4`}
                        style={{
                          color: "#1976d2",
                          marginTop: 3,
                        }}
                      >
                        ●
                      </span>
                      <Box className="text-left" style={{ width: "100%" }}>
                        {/* Se o item tiver um campo label, usar como título do item */}
                        {item.label && (
                          <Typography
                            variant="subtitle2"
                            style={{ fontWeight: "bold", marginBottom: 4 }}
                          >
                            {item.label}
                          </Typography>
                        )}

                        {/* Filtrar os campos de metadados */}
                        {Object.entries(item)
                          .filter(
                            ([itemKey]) =>
                              itemKey !== "label" &&
                              itemKey !== "type" &&
                              itemKey !== "description"
                          )
                          .map(([itemKey, itemValue]) => (
                            <RenderResultItem
                              key={itemKey}
                              keyName={itemKey}
                              value={itemValue}
                              depth={depth + 2}
                              path={`${nodePath}[${index}]`}
                              keyPath={currentKeyPath}
                              showEmptyItems={showEmptyItems}
                              templateData={templateData}
                              onFieldClick={onFieldClick}
                            />
                          ))}
                      </Box>
                    </Box>
                  </ListItem>
                );

                // Se não devemos mostrar itens vazios, verificar se o item tem conteúdo
                if (!showEmptyItems) {
                  const hasContent = Object.entries(item)
                    .filter(
                      ([itemKey]) =>
                        itemKey !== "label" &&
                        itemKey !== "type" &&
                        itemKey !== "description"
                    )
                    .some(([_, itemValue]) => {
                      if (itemValue === null || itemValue === undefined)
                        return false;
                      if (
                        typeof itemValue === "object" &&
                        !Array.isArray(itemValue)
                      ) {
                        return Object.keys(itemValue).length > 0;
                      }
                      if (Array.isArray(itemValue)) {
                        return itemValue.length > 0;
                      }
                      return true;
                    });

                  return hasContent ? renderedItem : null;
                }

                return renderedItem;
              } else {
                return (
                  <ListItem
                    key={index}
                    dense
                    disableGutters
                    className="text-left"
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      className="text-left"
                      style={{ width: "100%" }}
                    >
                      <span
                        className={`${classes.listBullet} mr-4`}
                        style={{
                          color: "#1976d2",
                          marginTop: 3,
                        }}
                      >
                        ●
                      </span>
                      <Typography variant="body2" align="left">
                        {String(item)}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              }
            })
            .filter(Boolean)
        : null;

    // Se não temos itens para mostrar e não devemos mostrar itens vazios, retornar null
    if (
      !showEmptyItems &&
      (!filteredItems || filteredItems.length === 0) &&
      depth > 0
    ) {
      return null;
    }

    return (
      <Box
        key={keyName}
        className="text-left"
        style={{
          paddingLeft: indentSize,
          marginBottom: depth === 0 ? 24 : 8,
        }}
      >
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={() => toggleNode(nodePath)}
            className="mr-4"
            style={{ padding: 4 }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {depth === 0 ? (
            renderItemTitle(null)
          ) : (
            <Typography
              variant="subtitle1"
              className="text-left"
              align="left"
            >
              <strong>{renderClickableLabel(displayLabel)}:</strong>
            </Typography>
          )}
        </Box>
        <Collapse in={isExpanded}>
          <List
            dense
            className={`${classes.resultList} text-left ml-8`}
            style={{
              borderLeft: "1px solid var(--ikaro-border-default)",
              paddingLeft: 8,
            }}
          >
            {filteredItems ? (
              filteredItems
            ) : (
              <ListItem dense disableGutters className="text-left">
                <Typography
                  variant="body2"
                  style={{ fontStyle: "italic", color: "var(--ikaro-text-muted)" }}
                >
                  Nenhum item encontrado
                </Typography>
              </ListItem>
            )}
          </List>
        </Collapse>
      </Box>
    );
  } else {
    // É um valor primitivo (string, número, booleano)
    return (
      <Box
        key={keyName}
        className={`${classes.resultItem} text-left`}
        style={{
          paddingLeft: indentSize,
          marginBottom: depth === 0 ? 24 : 8,
        }}
      >
        {depth === 0 ? (
          renderItemTitle(String(value))
        ) : (
          <Typography variant="body2" align="left">
            <strong>{renderClickableLabel(displayLabel)}:</strong> {String(value)}
          </Typography>
        )}
      </Box>
    );
  }
};

// Função para baixar os resultados como JSON

const BoxRenderResult = ({ result, rawResult = null, templateData = null }) => {
  const classes = useStyles();
  const [showRawResult, setShowRawResult] = useState(false);
  const [showEmptyItems, setShowEmptyItems] = useState(false);
  const [fieldDetailsOpen, setFieldDetailsOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const { t } = useTranslation("editor");
  const { t: tCommon } = useTranslation("common");
  
  // Handler para abrir o modal de detalhes do campo
  const handleFieldClick = (fieldInfo) => {
    setSelectedField(fieldInfo);
    setFieldDetailsOpen(true);
  };
  
  // Handler para fechar o modal de detalhes
  const handleCloseFieldDetails = () => {
    setFieldDetailsOpen(false);
    setSelectedField(null);
  };
  
  // Renderiza os detalhes do campo selecionado
  const renderFieldDetails = () => {
    if (!selectedField) return null;
    
    const { name, label, description, type, possible_values } = selectedField;
    
    return (
      <>
        {/* Nome do campo */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailLabel}>
            Nome do Campo
          </Typography>
          <Typography className={classes.detailValue}>
            {name || '-'}
          </Typography>
        </Box>
        
        {/* Label */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailLabel}>
            Label
          </Typography>
          <Typography className={`${classes.detailValue} ${!label ? classes.detailValueEmpty : ''}`}>
            {label || 'Não definido'}
          </Typography>
        </Box>
        
        {/* Descrição */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailLabel}>
            Descrição
          </Typography>
          <Typography className={`${classes.detailValue} ${!description ? classes.detailValueEmpty : ''}`}>
            {description || 'Não definido'}
          </Typography>
        </Box>
        
        {/* Tipo de dado */}
        {type && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailLabel}>
              Tipo de Dado
            </Typography>
            <Box className={classes.detailValue}>
              <Chip 
                label={getTypeLabel(type)} 
                size="small"
                className={classes.typeChip}
              />
            </Box>
          </Box>
        )}
        
        {/* Valores possíveis */}
        {possible_values && possible_values.length > 0 && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailLabel}>
              Valores Possíveis ({possible_values.length})
            </Typography>
            <Box className={classes.possibleValuesList}>
              {possible_values.map((value, index) => (
                <Chip
                  key={index}
                  label={value}
                  size="small"
                  className={classes.possibleValueItem}
                />
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  };

  const downloadResultsAsJson = () => {
    if (!result) return;

    try {
      // Determinar qual resultado usar baseado na seleção do usuário
      const dataToDownload = showRawResult && rawResult ? rawResult : result;

      // Criar uma string JSON formatada
      const jsonString = JSON.stringify(dataToDownload, null, 2);

      // Criar um blob com o conteúdo JSON
      const blob = new Blob([jsonString], { type: "application/json" });

      // Criar uma URL temporária para o blob
      const url = URL.createObjectURL(blob);

      // Criar um elemento de link para o download
      const link = document.createElement("a");
      link.href = url;
      link.download =
        showRawResult && rawResult ? "resultado-bruto.json" : "resultado.json";

      // Adicionar o link ao documento, clicar nele e removê-lo
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Liberar a URL do objeto
      URL.revokeObjectURL(url);

      console.log(t("downloadStarted"));
    } catch (error) {
      console.error("Erro ao baixar resultados como JSON:", error);
      alert(t("jsonError"));
    }
  };

  return (
    <Paper className={classes.resultContainer}>
      <Typography variant="h6" gutterBottom align="left">
        <Box
          fontWeight="fontWeightBold"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <span>{t("processingResult")}</span>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showEmptyItems}
                  onChange={(e) => setShowEmptyItems(e.target.checked)}
                  color="primary"
                />
              }
              label={t("showEmptyFields")}
              style={{ marginRight: 16 }}
            />
            {rawResult && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showRawResult}
                    onChange={(e) => setShowRawResult(e.target.checked)}
                    color="primary"
                  />
                }
                label={t("showRawResult")}
                style={{ marginRight: 16 }}
              />
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={resultHTML({ result, showRawResult, rawResult, t })}
              size="small"
              className="mr-8"
            >
              {t("printResult")}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetApp />}
              onClick={downloadResultsAsJson}
              size="small"
            >
              {t("saveResultJson")}
            </Button>
          </Box>
        </Box>
      </Typography>
      <Divider className="mb-16" />

      <Box className="text-left">
        {Object.entries(showRawResult && rawResult ? rawResult : result).map(
          ([key, value]) => (
            <RenderResultItem
              key={key}
              keyName={key}
              value={value}
              showEmptyItems={showEmptyItems}
              templateData={templateData}
              onFieldClick={templateData ? handleFieldClick : null}
            />
          )
        )}
      </Box>
      
      {/* Modal de detalhes do campo */}
      <Dialog
        open={fieldDetailsOpen}
        onClose={handleCloseFieldDetails}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.detailsDialogPaper }}
      >
        <DialogTitle className={classes.detailsDialogTitle}>
          Detalhes do Campo
        </DialogTitle>
        <DialogContent className={classes.detailsContent}>
          {renderFieldDetails()}
        </DialogContent>
        <DialogActions className={classes.detailsDialogActions}>
          <Button onClick={handleCloseFieldDetails} color="primary">
            {tCommon('close') || 'Fechar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export { RenderResultItem, BoxRenderResult };
